import { Controller } from "@hotwired/stimulus"

function bufferToBase64url(buffer) {
  const bytes = new Uint8Array(buffer)
  let str = ""
  for (const b of bytes) str += String.fromCharCode(b)
  return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "")
}

function base64urlToBuffer(base64url) {
  const base64 = base64url.replace(/-/g, "+").replace(/_/g, "/")
  const pad = base64.length % 4 === 0 ? "" : "=".repeat(4 - (base64.length % 4))
  const binary = atob(base64 + pad)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return bytes.buffer
}

export default class extends Controller {
  static targets = ["nickname", "status", "button"]

  async register(event) {
    event.preventDefault()

    this.#setLoading(true)

    try {
      const csrfToken = document.querySelector("meta[name='csrf-token']").content

      const challengeResponse = await fetch("/passkey_credentials/registration_challenge", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken }
      })
      const options = await challengeResponse.json()

      options.challenge = base64urlToBuffer(options.challenge)
      options.user.id = base64urlToBuffer(options.user.id)
      if (options.excludeCredentials) {
        options.excludeCredentials = options.excludeCredentials.map(c => ({
          ...c, id: base64urlToBuffer(c.id)
        }))
      }

      const credential = await navigator.credentials.create({ publicKey: options })

      if (!credential) {
        this.#setLoading(false)
        this.#setStatus("Your browser or device didn't return a passkey. Try again or use a different device.", "text-warning mt-2")
        return
      }

      const createResponse = await fetch("/passkey_credentials", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken },
        body: JSON.stringify({
          nickname: this.nicknameTarget.value,
          credential: {
            id: credential.id,
            rawId: bufferToBase64url(credential.rawId),
            type: credential.type,
            response: {
              attestationObject: bufferToBase64url(credential.response.attestationObject),
              clientDataJSON: bufferToBase64url(credential.response.clientDataJSON)
            }
          }
        })
      })

      const result = await createResponse.json()
      if (createResponse.ok) {
        this.#setStatus("Passkey registered!", "text-success mt-2")
        window.location.reload()
      } else {
        this.#setLoading(false)
        this.#setStatus(result.error || "Registration failed.", "text-danger mt-2")
      }
    } catch (err) {
      this.#setLoading(false)
      if (err.name === "NotAllowedError") {
        this.#setStatus("Registration was cancelled.", "text-warning mt-2")
      } else {
        this.#setStatus("Registration failed: " + err.message, "text-danger mt-2")
      }
    }
  }

  #setLoading(loading) {
    if (!this.hasButtonTarget) return
    this.buttonTarget.disabled = loading
    this.buttonTarget.textContent = loading ? "Registering…" : "Register Passkey"
  }

  #setStatus(message, className) {
    this.statusTarget.textContent = message
    this.statusTarget.className = className
  }
}
